'use strict';

define('vb/helpers/securityHelpers',[
  'vb/private/configLoader',
  'vb/private/utils',
  'vb/private/services/servicesManager',
  'vb/private/services/trapData',
  'vb/private/stateManagement/application',
  'vb/private/constants',
  'vbsw/private/tokenRelayUtils',
], (
  ConfigLoader,
  Utils,
  ServicesManager,
  TrapData,
  Application,
  Constants,
  TokenRelayUtils,
) => {
  /**
   * @category Helper
   */
  class SecurityHelpers {
    /**
     * Navigate to the login page specified by the given path.
     *
     * @param path
     * @returns {Promise}
     */
    /* static navigateToLoginPage(path) {
      return Application.navigateToPage({
        page: path,
      });
    } */

    /**
     * Navigate to the page specified by the given returnPath away from the login page. This call will replace the
     * navigation history so browser back will not navigate you back to the login page.
     *
     * @param returnPath
     * @returns {Promise}
     */
    /* static navigateToReturnPage(returnPath) {
      return Application.navigateToPage({
        page: returnPath,
        history: 'replace',  // make sure browser back won't navigate back to the login page.
      });
    } */

    /**
     * The method can be used to pass information such as username and password collected from the application to
     * the security provider.
     *
     * @param {Object} payload - An object payload that is understood by the underlying security provider.
     * @returns {Promise<any>} - A promise that resolves based on the security provider's response.
     */
    static securityCallback(payload) {
      return Application.securityProvider.securityCallback(payload);
    }

    /**
     * This method will return an array of plugin URLs from the DefaultSecurityProvider that are necessary for
     * FA applications to make REST requests when running in a VB environment. It will return an empty array
     * otherwise.
     *
     * Note that this method should only be called by FA's own implementation of the security provider.
     *
     * @returns {Promise<Array>} A promise that resolves to an array of plugin URLs if running in a VB environment,
     * or an empty array otherwise.
     */
    static getDefaultServiceWorkerPluginsForVbEnvironment() {
      return Promise.resolve().then(() => {
        const vbConfig = globalThis.vbInitConfig || {};
        const vbServer = vbConfig.VB_SERVER;

        // determine if we are running in a VB environment by checking if the VB_SERVER property
        // is injected into vbInitConfig
        if (vbServer) {
          // dynamically load the defaultSecurityProvider in order to get the list of default plugins
          return Utils.getResource('vb/private/types/defaultSecurityProvider')
            .then((DefaultSecurityProviderClass) => {
              const securityProvider = Application.securityProvider;

              // get the security provider from the application if exist in order to get to its
              // config object
              if (securityProvider) {
                const defaultSecurityProvider = new DefaultSecurityProviderClass();

                // directly set the idcsInfo instead of calling initialize
                const config = securityProvider.config || {};
                defaultSecurityProvider.idcsInfo = config.idcsInfo || {};

                return defaultSecurityProvider.getServiceWorkerPlugins(
                  config, !securityProvider.userInfo.isAuthenticated,
                );
              }

              return [];
            });
        }

        return [];
      });
    }

    /**
     * Get an access token for the given scope using the implicit flow grant type. A null
     * token will be returned if there is missing info necessary for requesting the token.
     *
     * @param {String} scope - The allowed scope for the access token.
     * @returns {Promise<String|null>} - A promise that resolves to the access token string
     * or null if missing necessary information.
     */
    static getImplicitFlowAccessToken(scope) {
      return Promise.resolve().then(() => {
        const { securityProvider } = Application;

        // make it a noop if idcsInfo is not available which can happen in DT or if the
        // app is not configured for implicit flow
        // NOTE: The check is done here to avoid changes to the internal api.
        const { idcsInfo } = securityProvider;
        if (!idcsInfo || !idcsInfo.hostName) {
          return null;
        }

        if (typeof securityProvider.vbRefreshImplicitFlowAccessToken === 'function') {
          return securityProvider.vbRefreshImplicitFlowAccessToken(scope);
        }

        throw new Error('Unsupported method for the current security provider');
      });
    }

    /**
     * Return a list of allowed scopes that are configured for implicit flow.
     *
     * This method retrieves the allowed scopes from the security provider's configuration. If the
     * `idcsInfo` object or the `allowedScopes` property is not available, it returns an empty array.
     *
     * @returns {String[]} - An array of strings representing the allowed scopes for implicit flow.
     * If no allowed scopes are configured, an empty array is returned.
     */
    static getImplicitFlowAllowedScopes() {
      const { securityProvider } = Application;
      const { idcsInfo } = securityProvider;

      return idcsInfo && idcsInfo.allowedScopes ? idcsInfo.allowedScopes : [];
    }

    /**
     * Return value of the access token from the service's token relay endpoint.
     *
     * @param {String} serviceName - The name of the service for which the access token is requested.
     * @param {Object} options - An object containing options for the token request.
     * @param {String} options.serviceName - Name of the service token relay endpoint is requested for.
     * @param {String} [options.extensionId = 'base'] - ID of the extension the service is defined in.
     *   If in the V2 app the extension ID is not provided, the service will be searched for in the
     *   base extension.
     * @param {String} [options.extensionVersion] - Version of the extension the service is defined in.
     *   If extension ID is provided but extension version is not, the extension version value will be
     *   the version of the referenced extension that is being used in the application.
     * @param {String} [options.activeProfile = ConfigLoader.activeProfile] - The active profile to be used.
     * @returns {Promise<String>} - A promise that resolves to the access token.
     * @deprecated Use SecurityHelpers.getServiceAccessToken instead.
     */
    static getTrapServiceAccessToken(serviceName, options) {
      return SecurityHelpers.getServiceAccessToken(serviceName, options);
    }

    /**
     * Return value of the access token from the service's token relay endpoint.
     *
     * @param {String} serviceName - The name of the service for which the access token is requested.
     * @param {Object} [options] - An object containing options for the token request.
     * @param {String} [options.serviceName] - Name of the service token relay endpoint is requested for.
     * @param {String} [options.extensionId='base'] - ID of the extension the service is defined in.
     *   If in the V2 app the extension ID is not provided, the service will be searched for in the base extension.
     * @param {String} [options.extensionVersion] - Version of the extension the service is defined in.
     *   If extension ID is provided but extension version is not, the extension version value will be
     *   the version of the referenced extension that is being used in the application.
     * @param {String} [options.activeProfile=ConfigLoader.activeProfile] - The active profile to be used.
     * @returns {Promise<String>} - A promise that resolves to the access token.
     */
    static getServiceAccessToken(serviceName, options = {}) {
      return Promise.resolve().then(() => {
        if (!serviceName) {
          throw new Error('Required service name argument missing.');
        }

        // See if the provided serviceName matches "<extensionId>:<serviceName>"
        // If so, use these values. Otherwise use serviceName as-is.
        const { prefix: extensionId, main /* , suffix */ } = Utils.parseQualifiedIdentifier(serviceName) || {};
        if (extensionId && options.extensionId && (options.extensionId !== extensionId)) {
          throw new Error('options.extensionId does not match extensionId.');
        }
        const mergeOptions = Utils.mergeObject({
          activeProfile: ConfigLoader.activeProfile,
          extensionId, // May be undefined
          serviceName: main || serviceName,
        },
        options);

        // If we were given extension ID but not its version, look for it in the ExtensionRegistry
        // In case extension ID is not provided we will look for the service in the unified app (extID: "base")
        if (mergeOptions.extensionId && !mergeOptions.extensionVersion) {
          const extensionRegistry = ConfigLoader.extensionRegistry;
          if (extensionRegistry) {
            const extension = extensionRegistry.getExtensionById(mergeOptions.extensionId);
            if (extension) {
              mergeOptions.extensionVersion = extension.version;
            }
          }
        }

        // Local overrides for this service will not work correctly
        // we need to resolve the service fully to gets its extension info.

        // Only client of this API is using it for IDCS Custom sign in
        // (https://confluence.oraclecorp.com/confluence/display/CCS/IDCS+Custom+Sign-In+Pages).
        // The app sign in is not something that is happening during app development,
        // so there will never be local overrides for these calls.
        // TODO: make this work for all possible inputs.
        const trapData = TrapData.getTrapData();

        const tokenRelayUrl = trapData.getTokenRelayUrl(mergeOptions);
        const isTrap2 = trapData.shouldUseTrapService();

        // If in unittests or the TokenRelayHandlerPlugin has not been configured, use fallback logic
        if (!TokenRelayUtils.useTokenRelayHandler()) {
          const trapRequest = Utils.createTokenRelayRequest(tokenRelayUrl, isTrap2);
          return globalThis.fetch(trapRequest).then((response) => {
            if (response.ok) {
              return response.json();
            }

            throw new Error(response.statusText);
          }).then((token) => {
            if (isTrap2) {
              return token.access_token;
            }
            // strip starting 'Bearer' prefix and return pure value
            return TokenRelayUtils.trimToken(token.authenticationHeader);
          });
        }

        // Use TokenRelayHandlerPlugin logic to retrieve token for the service
        return ServicesManager.getServiceAuthentication(serviceName)
          .then((tokenRelayAuthentication) => TokenRelayUtils.getTokenRelayAuthToken(tokenRelayUrl,
            tokenRelayAuthentication, isTrap2));
      });
    }

    /**
     * Return the metadata for the custom login page.
     *
     * This method fetches metadata information related to the custom login page
     * from the application's security provider.
     *
     * @returns {Object} - An object containing metadata for the custom login page.
     */
    static getCustomLoginInfo() {
      return Application.securityProvider.customLoginInfo;
    }

    /**
     * Store the path to where the application should be redirected after a successful login.
     *
     * This method saves the provided path to session storage. It is used to remember
     * the desired redirection path after the user successfully logs in to the application.
     *
     * @param {String} path - The path to be stored in session storage.
     */
    static storeCustomLoginSuccessPath(path) {
      sessionStorage.setItem(Constants.SessionStorage.CUSTOM_LOGIN_SUCCESS_PATH, path);
    }

    /**
     * Retrieve the path to where the application should be redirected after a successful login.
     *
     * This method fetches the stored path from session storage where the application should redirect
     * post-login success. Once the path is retrieved, it is removed from session storage to ensure
     * it is only used once.
     *
     * @returns {String} - The path to redirect to after a successful login or `null` if no path is stored.
     */
    static retrieveCustomLoginSuccessPath() {
      const successPath = sessionStorage.getItem(Constants.SessionStorage.CUSTOM_LOGIN_SUCCESS_PATH);

      sessionStorage.removeItem(Constants.SessionStorage.CUSTOM_LOGIN_SUCCESS_PATH);

      return successPath;
    }

    /**
     * Return the profile object for the current user.
     *
     * This method retrieves the profile information of the currently authenticated user from the application's
     * security provider.
     *
     * @returns {Object} - An object containing user profile information.
     */
    static getUserInfo() {
      return Application.securityProvider.userInfo;
    }
  }

  return SecurityHelpers;
});

